/**
 * Document Export Service
 * Handles DOCX generation with professional transcription report formatting
 */

import * as docx from 'docx';
import { saveAs } from 'file-saver';
import { APP_VERSION } from '../config/constants';
import { ExportOptions } from '../types';

// Brand colours
const COLORS = {
  primary: '4f46e5',      // Indigo
  primaryDark: '3730a3',  // Dark indigo
  secondary: '6366f1',    // Light indigo
  accent: '10b981',       // Emerald
  text: '1e293b',         // Slate 800
  textLight: '64748b',    // Slate 500
  textMuted: '94a3b8',    // Slate 400
  background: 'f8fafc',   // Slate 50
  backgroundAlt: 'f1f5f9', // Slate 100
  border: 'e2e8f0',       // Slate 200
  white: 'ffffff',
};

/**
 * Export transcript data to DOCX format
 */
export async function exportToDocx(options: ExportOptions): Promise<void> {
  const { outputFileName, speakerMap, intelligence, finalTranscript } = options;

  const doc = new docx.Document({
    creator: `LinguistPro ${APP_VERSION}`,
    title: 'Transcription Report',
    description: 'Automated transcription record with speaker labelling',
    styles: {
      default: {
        document: {
          run: {
            font: 'Calibri',
            size: 22,
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1134, bottom: 1134, left: 1134, right: 1134 }, // 2cm margins
          },
        },
        headers: {
          default: createDocumentHeader(),
        },
        footers: {
          default: createDocumentFooter(),
        },
        children: [
          ...createCoverSection(outputFileName),
          ...createTableOfContents(),
          createSectionBreak(),
          ...createSpeakerSection(speakerMap),
          createSectionBreak(),
          ...(intelligence ? createIntelligenceSection(intelligence) : []),
          ...(intelligence ? [createSectionBreak()] : []),
          ...createTranscriptSection(finalTranscript),
          ...createEndSection(),
        ],
      },
    ],
  });

  const blob = await docx.Packer.toBlob(doc);
  saveAs(blob, `${outputFileName}_TRANSCRIPTION_REPORT.docx`);
}

/**
 * Export transcript to SRT subtitle format.
 * Parses the formatted transcript (lines: "[MM:SS] **Speaker**: text")
 * and builds an SRT file with gap-free timing (each entry ends when the next begins).
 */
export function exportToSrt(finalTranscript: string, outputFileName: string): void {
  // Regex: [MM:SS] or [HH:MM:SS], optional **Speaker**: text
  const LINE_RE = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*\*?\*?(.+?)\*?\*?:\s*(.+)$/;

  /** Convert "MM:SS" or "HH:MM:SS" to total seconds */
  const toSeconds = (ts: string): number => {
    const parts = ts.split(':').map(Number);
    return parts.length === 3
      ? parts[0] * 3600 + parts[1] * 60 + parts[2]
      : parts[0] * 60 + parts[1];
  };

  /** Format seconds to SRT timestamp: HH:MM:SS,mmm */
  const toSrtTime = (sec: number): string => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    const ms = Math.round((sec % 1) * 1000);
    return [
      String(h).padStart(2, '0'),
      String(m).padStart(2, '0'),
      String(s).padStart(2, '0'),
    ].join(':') + ',' + String(ms).padStart(3, '0');
  };

  // Parse timed lines from the formatted transcript
  type Entry = { start: number; speaker: string; text: string };
  const entries: Entry[] = [];

  for (const line of finalTranscript.split('\n')) {
    const m = LINE_RE.exec(line.trim());
    if (m) {
      entries.push({ start: toSeconds(m[1]), speaker: m[2].trim(), text: m[3].trim() });
    }
  }

  if (entries.length === 0) {
    // Fallback: no timestamps found — export plain text as a single block
    const blob = new Blob([`1\n00:00:00,000 --> 00:00:10,000\n${finalTranscript.trim()}\n`], {
      type: 'text/plain;charset=utf-8',
    });
    saveAs(blob, `${outputFileName}.srt`);
    return;
  }

  // Build SRT blocks — end time = next entry's start (last entry gets +3s)
  const blocks: string[] = entries.map((entry, i) => {
    const end = i + 1 < entries.length ? entries[i + 1].start : entry.start + 3;
    return [
      String(i + 1),
      `${toSrtTime(entry.start)} --> ${toSrtTime(end)}`,
      `${entry.speaker}: ${entry.text}`,
      '',
    ].join('\n');
  });

  const blob = new Blob([blocks.join('\n')], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, `${outputFileName}.srt`);
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

// --- Document Header & Footer ---

function createDocumentHeader(): docx.Header {
  return new docx.Header({
    children: [
      new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: 'LINGUISTPRO',
            bold: true,
            size: 16,
            color: COLORS.primary,
            font: 'Calibri',
          }),
          new docx.TextRun({
            text: '  |  TRANSCRIPTION RECORD',
            size: 14,
            color: COLORS.textMuted,
            font: 'Calibri',
          }),
        ],
        border: {
          bottom: { color: COLORS.border, space: 1, style: docx.BorderStyle.SINGLE, size: 6 },
        },
        spacing: { after: 200 },
      }),
    ],
  });
}

function createDocumentFooter(): docx.Footer {
  return new docx.Footer({
    children: [
      new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: 'CONFIDENTIAL',
            size: 14,
            color: COLORS.textMuted,
            font: 'Calibri',
          }),
          new docx.TextRun({
            text: '\t\t',
          }),
          new docx.TextRun({
            children: ['Page ', docx.PageNumber.CURRENT, ' of ', docx.PageNumber.TOTAL_PAGES],
            size: 14,
            color: COLORS.textMuted,
            font: 'Calibri',
          }),
        ],
        border: {
          top: { color: COLORS.border, space: 1, style: docx.BorderStyle.SINGLE, size: 6 },
        },
        spacing: { before: 200 },
        tabStops: [
          { type: docx.TabStopType.RIGHT, position: docx.convertInchesToTwip(6) },
        ],
      }),
    ],
  });
}

// --- Cover Section ---

function createCoverSection(outputFileName: string): (docx.Paragraph | docx.Table)[] {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const timeStr = now.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return [
    // Logo/Brand
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: '◆',
          size: 48,
          color: COLORS.primary,
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { before: 600, after: 200 },
    }),
    // Main Title
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'TRANSCRIPTION',
          bold: true,
          size: 56,
          color: COLORS.text,
          font: 'Calibri Light',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 0 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'REPORT',
          bold: true,
          size: 56,
          color: COLORS.primary,
          font: 'Calibri Light',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
    // Subtitle
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'Automated Transcription with Speaker Labelling',
          size: 24,
          color: COLORS.textLight,
          font: 'Calibri',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 800 },
    }),
    // Metadata Box
    createMetadataBox(outputFileName, dateStr, timeStr),
    // Spacer
    new docx.Paragraph({ spacing: { after: 400 } }),
  ];
}

function createMetadataBox(fileName: string, date: string, time: string): docx.Table {
  return new docx.Table({
    width: { size: 60, type: docx.WidthType.PERCENTAGE },
    alignment: docx.AlignmentType.CENTER,
    borders: {
      top: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideHorizontal: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
    },
    rows: [
      createMetadataRow('ASSET IDENTIFIER', fileName, true),
      createMetadataRow('GENERATION DATE', date, false),
      createMetadataRow('GENERATION TIME', time, false),
      createMetadataRow('ENGINE VERSION', APP_VERSION, false),
      createMetadataRow('CLASSIFICATION', 'CONFIDENTIAL', false),
    ],
  });
}

function createMetadataRow(label: string, value: string, isFirst: boolean): docx.TableRow {
  return new docx.TableRow({
    children: [
      new docx.TableCell({
        children: [
          new docx.Paragraph({
            children: [
              new docx.TextRun({
                text: label,
                bold: true,
                size: 18,
                color: COLORS.textMuted,
                font: 'Calibri',
              }),
            ],
            spacing: { before: 80, after: 80 },
            indent: { left: 200 },
          }),
        ],
        width: { size: 40, type: docx.WidthType.PERCENTAGE },
        shading: { fill: COLORS.background },
        verticalAlign: docx.VerticalAlign.CENTER,
      }),
      new docx.TableCell({
        children: [
          new docx.Paragraph({
            children: [
              new docx.TextRun({
                text: value,
                bold: isFirst,
                size: isFirst ? 22 : 20,
                color: isFirst ? COLORS.primary : COLORS.text,
                font: 'Calibri',
              }),
            ],
            spacing: { before: 80, after: 80 },
            indent: { left: 200 },
          }),
        ],
        width: { size: 60, type: docx.WidthType.PERCENTAGE },
        verticalAlign: docx.VerticalAlign.CENTER,
      }),
    ],
  });
}

// --- Table of Contents ---

function createTableOfContents(): docx.Paragraph[] {
  return [
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'DOCUMENT CONTENTS',
          bold: true,
          size: 28,
          color: COLORS.text,
          font: 'Calibri',
        }),
      ],
      spacing: { before: 400, after: 300 },
      border: {
        bottom: { color: COLORS.primary, space: 1, style: docx.BorderStyle.SINGLE, size: 12 },
      },
    }),
    createTocEntry('1', 'Speaker Map', 'Speakers as labelled in this recording'),
    createTocEntry('2', 'Intelligence Synthesis', 'AI-generated analysis and insights'),
    createTocEntry('3', 'Verbatim Transcript', 'Complete timestamped dialogue record'),
  ];
}

function createTocEntry(num: string, title: string, desc: string): docx.Paragraph {
  return new docx.Paragraph({
    children: [
      new docx.TextRun({
        text: `${num}.  `,
        bold: true,
        size: 22,
        color: COLORS.primary,
        font: 'Calibri',
      }),
      new docx.TextRun({
        text: title,
        bold: true,
        size: 22,
        color: COLORS.text,
        font: 'Calibri',
      }),
      new docx.TextRun({
        text: `  —  ${desc}`,
        size: 20,
        color: COLORS.textLight,
        font: 'Calibri',
      }),
    ],
    spacing: { before: 120, after: 120 },
    indent: { left: 400 },
  });
}

// --- Section Break ---

function createSectionBreak(): docx.Paragraph {
  return new docx.Paragraph({
    children: [
      new docx.TextRun({
        text: '───────────────────────────────────────────────────',
        color: COLORS.border,
        size: 20,
      }),
    ],
    alignment: docx.AlignmentType.CENTER,
    spacing: { before: 600, after: 600 },
  });
}

// --- Speaker Section ---

function createSpeakerSection(speakerMap: string): (docx.Paragraph | docx.Table)[] {
  const header = createSectionHeader('1', 'SPEAKER MAP', 'Speakers as labelled in this recording');

  const speakers = speakerMap
    .split('\n')
    .filter((l) => l.trim())
    .map((line, idx) => {
      const cleaned = line.replace(/^\*\s*/, '').replace(/\*\*/g, '');
      const [name, ...descParts] = cleaned.split(':');
      return {
        name: name.trim(),
        description: descParts.join(':').trim(),
        index: idx,
      };
    });

  const rows = speakers.map((speaker, idx) => {
    const isEven = idx % 2 === 0;
    return new docx.TableRow({
      children: [
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({
                  text: `${idx + 1}`,
                  bold: true,
                  size: 20,
                  color: COLORS.white,
                  font: 'Calibri',
                }),
              ],
              alignment: docx.AlignmentType.CENTER,
              spacing: { before: 100, after: 100 },
            }),
          ],
          width: { size: 8, type: docx.WidthType.PERCENTAGE },
          shading: { fill: COLORS.primary },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({
                  text: speaker.name || `Speaker ${idx + 1}`,
                  bold: true,
                  size: 22,
                  color: COLORS.text,
                  font: 'Calibri',
                }),
              ],
              spacing: { before: 100, after: 100 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 32, type: docx.WidthType.PERCENTAGE },
          shading: { fill: isEven ? COLORS.background : COLORS.white },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({
                  text: speaker.description || 'No additional information',
                  size: 20,
                  color: COLORS.textLight,
                  font: 'Calibri',
                }),
              ],
              spacing: { before: 100, after: 100 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 60, type: docx.WidthType.PERCENTAGE },
          shading: { fill: isEven ? COLORS.background : COLORS.white },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
      ],
    });
  });

  const table = new docx.Table({
    width: { size: 100, type: docx.WidthType.PERCENTAGE },
    borders: {
      top: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
    },
    rows: [
      // Header row
      new docx.TableRow({
        children: [
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: '#',
                    bold: true,
                    size: 18,
                    color: COLORS.white,
                    font: 'Calibri',
                  }),
                ],
                alignment: docx.AlignmentType.CENTER,
                spacing: { before: 80, after: 80 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: 'SPEAKER',
                    bold: true,
                    size: 18,
                    color: COLORS.white,
                    font: 'Calibri',
                  }),
                ],
                spacing: { before: 80, after: 80 },
                indent: { left: 200 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [
                  new docx.TextRun({
                    text: 'IDENTIFICATION METHOD',
                    bold: true,
                    size: 18,
                    color: COLORS.white,
                    font: 'Calibri',
                  }),
                ],
                spacing: { before: 80, after: 80 },
                indent: { left: 200 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
        ],
      }),
      ...rows,
    ],
  });

  return [...header, table];
}

// --- Intelligence Section ---

function createIntelligenceSection(intelligence: string): (docx.Paragraph | docx.Table)[] {
  const header = createSectionHeader('2', 'INTELLIGENCE SYNTHESIS', 'AI-Generated Analysis');

  const paragraphs = intelligence
    .split('\n')
    .filter((l) => l.trim())
    .map((line) => {
      const isHeading = line.startsWith('##') || line.startsWith('**');
      const cleanLine = line.replace(/^#+\s*/, '').replace(/\*\*/g, '');
      
      return new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: isHeading ? '▸ ' : '',
            color: COLORS.primary,
            size: 22,
          }),
          new docx.TextRun({
            text: cleanLine,
            bold: isHeading,
            size: isHeading ? 24 : 22,
            color: isHeading ? COLORS.text : COLORS.textLight,
            font: 'Calibri',
          }),
        ],
        spacing: { before: isHeading ? 200 : 80, after: 80 },
        indent: { left: isHeading ? 0 : 400 },
      });
    });

  return [...header, ...paragraphs];
}

// --- Transcript Section ---

function createTranscriptSection(finalTranscript: string): docx.Paragraph[] {
  const header = createSectionHeader('3', 'VERBATIM TRANSCRIPT', 'Complete Timestamped Record');

  const lines = finalTranscript.split('\n').filter((l) => l.trim());
  
  const paragraphs = lines.map((line) => {
    // Check for timestamp pattern [MM:SS] or [HH:MM:SS]
    const timestampMatch = line.match(/^\[(\d+:\d+(?::\d+)?)\]/);
    // Check for speaker pattern (Name: or **Name:**)
    const speakerMatch = line.match(/^\[?\d*:?\d*\]?\s*\*?\*?([A-Za-z\s]+?)(?:\*\*)?:\s*/);
    
    if (timestampMatch) {
      const timestamp = timestampMatch[1];
      const rest = line.substring(timestampMatch[0].length);
      
      return new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: `[${timestamp}]`,
            bold: true,
            size: 18,
            color: COLORS.primary,
            font: 'Consolas',
          }),
          new docx.TextRun({
            text: `  ${rest}`,
            size: 21,
            color: COLORS.text,
            font: 'Calibri',
          }),
        ],
        spacing: { before: 160, after: 80 },
        shading: { fill: COLORS.backgroundAlt },
        indent: { left: 200, right: 200 },
      });
    } else if (speakerMatch) {
      const speaker = speakerMatch[1].trim();
      const dialogue = line.substring(speakerMatch[0].length);
      
      return new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: `${speaker}: `,
            bold: true,
            size: 21,
            color: COLORS.primaryDark,
            font: 'Calibri',
          }),
          new docx.TextRun({
            text: dialogue,
            size: 21,
            color: COLORS.text,
            font: 'Calibri',
          }),
        ],
        spacing: { before: 80, after: 80 },
        indent: { left: 400 },
      });
    } else {
      return new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: line,
            size: 21,
            color: COLORS.text,
            font: 'Calibri',
          }),
        ],
        spacing: { before: 80, after: 80 },
        indent: { left: 400 },
      });
    }
  });

  return [...header, ...paragraphs];
}

// --- Section Header Helper ---

function createSectionHeader(num: string, title: string, subtitle: string): docx.Paragraph[] {
  return [
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: `SECTION ${num}`,
          bold: true,
          size: 18,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      spacing: { before: 400, after: 80 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: title,
          bold: true,
          size: 32,
          color: COLORS.text,
          font: 'Calibri Light',
        }),
      ],
      spacing: { after: 40 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: subtitle,
          size: 22,
          color: COLORS.textLight,
          font: 'Calibri',
        }),
      ],
      spacing: { after: 300 },
      border: {
        bottom: { color: COLORS.primary, space: 1, style: docx.BorderStyle.SINGLE, size: 8 },
      },
    }),
  ];
}

// --- End Section ---

function createEndSection(): docx.Paragraph[] {
  return [
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: '◆ ◆ ◆',
          size: 28,
          color: COLORS.primary,
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { before: 800, after: 200 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'END OF TRANSCRIPT',
          bold: true,
          size: 20,
          color: COLORS.textMuted,
          font: 'Calibri',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 100 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: `Generated by LinguistPro ${APP_VERSION}`,
          size: 18,
          italics: true,
          color: COLORS.textMuted,
          font: 'Calibri',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 100 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'This document is auto-generated and should be verified for accuracy.',
          size: 16,
          color: COLORS.textMuted,
          font: 'Calibri',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
    }),
  ];
}
